/**
 * Client-side visibility helpers for navigation. This is UX only – the API
 * enforces every permission again (a hidden menu item is not a security measure).
 *
 * @typedef {{ roles?: string[], permissions?: string[] }} AccessRule
 *   Visible if the user has ANY of the listed roles OR ANY of the listed permissions.
 *   An undefined/empty rule means "visible for every authenticated user".
 * @typedef {{ roles?: string[], permissions?: string[] }} AccessSubject  shape of GET /me data
 */

export const SUPER_ADMIN_ROLE = 'super-admin';

/**
 * @param {AccessSubject|null|undefined} me
 * @param {string} permission
 */
export function hasPermission(me, permission) {
  if (!me) return false;
  if (me.roles?.includes(SUPER_ADMIN_ROLE)) return true;
  return Boolean(me.permissions?.includes(permission));
}

/**
 * @param {AccessSubject|null|undefined} me
 * @param {string} role
 */
export function hasRole(me, role) {
  return Boolean(me?.roles?.includes(role));
}

/**
 * @param {AccessSubject|null|undefined} me
 * @param {AccessRule|undefined} rule
 */
export function canAccess(me, rule) {
  if (!me) return false;
  if (!rule) return true;
  const roles = rule.roles ?? [];
  const permissions = rule.permissions ?? [];
  if (roles.length === 0 && permissions.length === 0) return true;
  if (me.roles?.includes(SUPER_ADMIN_ROLE)) return true;
  return roles.some((r) => hasRole(me, r)) || permissions.some((p) => hasPermission(me, p));
}

/**
 * Filters a navigation config. Items may be grouped in sections:
 * `[{ id, label?, items: [{ id, label, to, access }] }]`. Sections without
 * visible items are removed.
 * @template {{ access?: AccessRule }} T
 * @param {Array<{ items: T[] } & Record<string, any>>} sections
 * @param {AccessSubject|null|undefined} me
 */
export function filterNavSections(sections, me) {
  return sections.map((section) => ({ ...section, items: filterNavItems(section.items, me) })).filter((section) => section.items.length > 0);
}

/**
 * @template {{ access?: AccessRule }} T
 * @param {T[]} items
 * @param {AccessSubject|null|undefined} me
 * @returns {T[]}
 */
export function filterNavItems(items, me) {
  return items.filter((item) => canAccess(me, item.access));
}

/**
 * Picks the items for a mobile bottom navigation: at most `max` items, ordered
 * by their `mobilePriority` (lower first; items without priority are skipped),
 * the rest goes to "Mehr".
 * @template {{ mobilePriority?: number }} T
 * @param {T[]} visibleItems  already filtered items
 * @param {number} [max=4]
 * @returns {{ primary: T[], more: T[] }}
 */
export function splitBottomNav(visibleItems, max = 4) {
  const ranked = visibleItems
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => typeof item.mobilePriority === 'number')
    .sort((a, b) => a.item.mobilePriority - b.item.mobilePriority || a.index - b.index)
    .slice(0, max)
    .map(({ item }) => item);
  const more = visibleItems.filter((item) => !ranked.includes(item));
  return { primary: ranked, more };
}
